import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackSmartPageView, trackAnonymousClientError } from '../utils/actionTracker';
import { resolveRouteAnalytics } from '../utils/routePageCatalog';
import { ga4PageView, initOptionalGa4 } from '../utils/optionalGa4';

const DEDUPE_MS = 2000;

/**
 * Fires smart page_view (Firestore) + optional GA4 on SPA navigations.
 * Mount once inside `<Router>`.
 */
const AnalyticsRouteTracker: React.FC = () => {
   const location = useLocation();
   const lastKeyRef = useRef<string>('');
   const lastAtRef = useRef<number>(0);

   useEffect(() => {
      initOptionalGa4();
   }, []);

   useEffect(() => {
      const path = location.pathname + (location.search || '');
      const now = Date.now();
      if (path === lastKeyRef.current && now - lastAtRef.current < DEDUPE_MS) {
         return;
      }
      lastKeyRef.current = path;
      lastAtRef.current = now;

      const { pageTitle, routeTemplate, featureArea } = resolveRouteAnalytics(location.pathname);
      const pagePath = `${location.pathname}${location.search || ''}`;

      void trackSmartPageView(pagePath, pageTitle, {
         routeTemplate,
         featureArea,
         hash: location.hash || '',
      });
      ga4PageView(pagePath, pageTitle);
   }, [location.pathname, location.search, location.hash]);

   return null;
};

let errorHandlersInstalled = false;

/** Best-effort client error reporting (anonymousActions). Safe to call once at app shell. */
export function installGlobalAnalyticsErrorHandlers(): void {
   if (typeof window === 'undefined' || errorHandlersInstalled) return;
   errorHandlersInstalled = true;

   window.addEventListener('error', (ev: ErrorEvent) => {
      const msg = ev.message || 'window_error';
      void trackAnonymousClientError(msg, {
         filename: ev.filename,
         lineno: ev.lineno,
         colno: ev.colno,
         errorName: ev.error?.name,
      });
   });

   window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
      const reason = ev.reason;
      const msg =
         typeof reason === 'string'
            ? reason
            : reason instanceof Error
              ? reason.message
              : 'unhandledrejection';
      void trackAnonymousClientError(msg, {
         type: 'unhandledrejection',
         stack: reason instanceof Error ? reason.stack : undefined,
      });
   });
}

export default AnalyticsRouteTracker;
